import { useMemo } from "react"
import { Path, useFormContext } from "react-hook-form"

import useIsDesktop from "@hooks/useIsDesktop"
import useUser from "@hooks/useUser"

import DrawerHeader from "@atoms/BottomDrawer/DrawerHeader/DrawerHeader"
import { BottomDrawer, Checkbox } from "@atoms/index"

import { InputName } from "@molecules/Form/inputs/Input/Input.types"
import ListItem from "@molecules/ListItem/ListItem"
import ListItemContent from "@molecules/ListItem/ListItemContent/ListItemContent"

import { Settings } from "@templates/index"

import EmailNotificationTooltip from "@pages/Settings/SettingsNotifications/components/EmailNotificationTooltip/EmailNotificationTooltip"
import { useNotificationMethodsQuery } from "@pages/Settings/SettingsNotifications/components/NotificationMethodsSection/NotificationMethodsSection.store"
import SMSNotificationTooltip from "@pages/Settings/SettingsNotifications/components/SMSNotificationTooltip/SMSNotificationTooltip"

import styles from "./NotificationProperty.module.scss"
import { NotificationPropertyProps } from "./NotificationProperty.types"

export default function NotificationProperty<T extends InputName>(props: NotificationPropertyProps<T>) {
    const {
        label,
        supportText,
        isLoading,
        emailCheckboxName,
        smsCheckboxName,
        isControlled = true,
        isEmailChecked,
        isEmailIndeterminate,
        onEmailCheckedChange,
        isSmsChecked,
        isSmsIndeterminate,
        onSmsCheckedChange,
    } = props

    const isDesktop = useIsDesktop()
    const { user } = useUser()
    const form = useFormContext<T>()

    const { data: defaultMethodsPreference } = useNotificationMethodsQuery()

    const phoneNotificationsDisabled = !user?.phone || !user?.phone_verified || !defaultMethodsPreference?.contact_sms
    const emailNotificationsDisabled =
        !user?.email || !user?.email_verified || !defaultMethodsPreference?.contact_email
    const emailIsNotVerified = !!(user && !user?.email_verified)
    const phoneIsNotSet = !!(user && !user?.phone)
    const phoneIsNotVerified = !!(user && !user?.phone_verified)

    const emailCheckedValue =
        isControlled && emailCheckboxName && form ? (form.watch(emailCheckboxName as Path<T>) as boolean) : false
    const smsCheckedValue =
        isControlled && smsCheckboxName && form ? (form.watch(smsCheckboxName as Path<T>) as boolean) : false

    const notificationValue = useMemo(() => {
        let emailStatus = ""
        let smsStatus = ""

        if (isControlled && emailCheckboxName && smsCheckboxName) {
            emailStatus = emailCheckedValue && !emailNotificationsDisabled ? "Email" : ""
            smsStatus = smsCheckedValue && !phoneNotificationsDisabled ? "Text" : ""
        } else if (!isControlled) {
            emailStatus = !emailNotificationsDisabled ? "Email" : ""
            smsStatus = !phoneNotificationsDisabled ? "Text" : ""
        }

        if (emailStatus && smsStatus) {
            return "Email, Text"
        } else if (emailStatus || smsStatus) {
            return emailStatus || smsStatus
        } else {
            return "Off"
        }
    }, [
        isControlled,
        emailCheckboxName,
        smsCheckboxName,
        emailCheckedValue,
        smsCheckedValue,
        emailNotificationsDisabled,
        phoneNotificationsDisabled,
        isEmailChecked,
        isSmsChecked,
    ])

    if (isDesktop) {
        return (
            <ListItem.Root>
                <ListItem.AvatarAndContent
                    content={
                        <ListItemContent
                            leftLabel={<ListItem.Label label={label} />}
                            leftCaption={
                                supportText ? <ListItem.Caption caption={supportText} truncate={false} /> : null
                            }
                        />
                    }
                />
                <ListItem.Actions>
                    <Settings.NotificationConfigPage.Checkboxes>
                        <EmailNotificationTooltip>
                            <Checkbox<T>
                                isDisabled={isLoading || emailNotificationsDisabled}
                                checked={
                                    isEmailIndeterminate
                                        ? "indeterminate"
                                        : (isEmailChecked ?? emailCheckedValue ?? undefined)
                                }
                                onCheckedChange={onEmailCheckedChange}
                                name={emailCheckboxName}
                                isControlled={emailNotificationsDisabled ? false : isControlled}
                                label="Email"
                                showLabel={true}
                            />
                        </EmailNotificationTooltip>
                        <SMSNotificationTooltip>
                            <Checkbox<T>
                                isDisabled={isLoading || phoneNotificationsDisabled}
                                checked={
                                    isSmsIndeterminate
                                        ? "indeterminate"
                                        : (isSmsChecked ?? smsCheckedValue ?? undefined)
                                }
                                onCheckedChange={onSmsCheckedChange}
                                name={smsCheckboxName}
                                isControlled={phoneNotificationsDisabled ? false : isControlled}
                                label="Text"
                                showLabel={true}
                            />
                        </SMSNotificationTooltip>
                    </Settings.NotificationConfigPage.Checkboxes>
                </ListItem.Actions>
            </ListItem.Root>
        )
    } else {
        return (
            <>
                <BottomDrawer.Root>
                    <BottomDrawer.Trigger asChild={true}>
                        <ListItem.Root isClickable={true} onClick={() => {}}>
                            <ListItem.AvatarAndContent
                                content={
                                    <ListItemContent
                                        leftLabel={<ListItem.Label label={label} />}
                                        leftCaption={
                                            supportText ? (
                                                <ListItem.Caption caption={supportText} truncate={false} />
                                            ) : null
                                        }
                                        rightLabel={<ListItem.Label label={notificationValue} variant="subtle" />}
                                    />
                                }
                            />
                            <ListItem.Actions showChevron={true} />
                        </ListItem.Root>
                    </BottomDrawer.Trigger>
                    <BottomDrawer.Content>
                        <DrawerHeader.Root>
                            <DrawerHeader.Title title={label} />
                        </DrawerHeader.Root>
                        <Settings.NotificationConfigPage.Checkboxes>
                            <EmailNotificationTooltip>
                                <Checkbox<T>
                                    isDisabled={isLoading || emailNotificationsDisabled}
                                    checked={
                                        isEmailIndeterminate
                                            ? "indeterminate"
                                            : (isEmailChecked ?? emailCheckedValue ?? undefined)
                                    }
                                    onCheckedChange={onEmailCheckedChange}
                                    name={emailCheckboxName}
                                    isControlled={emailNotificationsDisabled ? false : isControlled}
                                    label="Email"
                                    showLabel={true}
                                    variant="card"
                                    className={styles.checkbox}
                                    supportText={
                                        emailIsNotVerified ? "Verification required." : "Email notifications disabled."
                                    }
                                    showSupportText={emailNotificationsDisabled || emailIsNotVerified}
                                />
                            </EmailNotificationTooltip>
                            <SMSNotificationTooltip>
                                <Checkbox<T>
                                    isDisabled={isLoading || phoneNotificationsDisabled}
                                    checked={
                                        isSmsIndeterminate
                                            ? "indeterminate"
                                            : (isSmsChecked ?? smsCheckedValue ?? undefined)
                                    }
                                    onCheckedChange={onSmsCheckedChange}
                                    name={smsCheckboxName}
                                    isControlled={phoneNotificationsDisabled ? false : isControlled}
                                    label="Text"
                                    showLabel={true}
                                    variant="card"
                                    className={styles.checkbox}
                                    supportText={
                                        phoneIsNotSet
                                            ? "Phone number not set."
                                            : phoneIsNotVerified
                                              ? "Verification required."
                                              : "Text notifications disabled."
                                    }
                                    showSupportText={phoneNotificationsDisabled || phoneIsNotVerified}
                                />
                            </SMSNotificationTooltip>
                        </Settings.NotificationConfigPage.Checkboxes>
                    </BottomDrawer.Content>
                </BottomDrawer.Root>
            </>
        )
    }
}
